const DEFAULT_COLOR = '#E05B8F';
const Y_LABEL_WIDTH = 36;
const PAD_TOP = 8;
const PAD_BOTTOM = 24; // room for x-axis labels
const PAD_RIGHT = 8;

interface Reading {
  date: string | null;
  value: number;
}

interface VitalsSvgGraphProps {
  readings: Reading[];
  color?: string;
  height?: number;
  yAxisLabel?: string;
}

function formatAxisDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(new Date(dateStr));
}

export function VitalsSvgGraph({
  readings,
  color = DEFAULT_COLOR,
  height = 180,
  yAxisLabel,
}: VitalsSvgGraphProps) {
  if (readings.length === 0) return null;

  const values = readings.map((r) => r.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const innerTop = PAD_TOP;
  const innerBottom = height - PAD_BOTTOM;
  const innerH = innerBottom - innerTop;

  function yForVal(v: number): number {
    return innerTop + innerH - ((v - min) / range) * innerH;
  }

  // viewBox width — use 100% width, we'll set it via the SVG viewBox trick
  // We work in a fixed coordinate space; SVG width="100%" preserveAspectRatio="none"
  const VW = 300;
  const innerLeft = Y_LABEL_WIDTH;
  const innerRight = VW - PAD_RIGHT;
  const innerW = innerRight - innerLeft;

  const step = readings.length > 1 ? innerW / (readings.length - 1) : 0;

  const points = readings.map((r, i) => {
    const x = innerLeft + (readings.length === 1 ? innerW / 2 : i * step);
    const y = yForVal(r.value);
    return { x, y, r };
  });

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');

  // Area polygon: line points + bottom-right + bottom-left
  const firstPt = points[0];
  const lastPt = points[points.length - 1];
  const areaPoints = [
    ...points.map((p) => `${p.x},${p.y}`),
    `${lastPt.x},${innerBottom}`,
    `${firstPt.x},${innerBottom}`,
  ].join(' ');

  // Y-axis guides at min, mid, max
  const guides = [
    { val: min, y: yForVal(min) },
    { val: (min + max) / 2, y: yForVal((min + max) / 2) },
    { val: max, y: yForVal(max) },
  ];

  const gradId = `vg-grad-${color.replace('#', '')}`;

  return (
    <svg
      viewBox={`0 0 ${VW} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Y-axis guide lines */}
      {guides.map(({ val, y }) => (
        <g key={val}>
          <line
            x1={innerLeft}
            y1={y}
            x2={innerRight}
            y2={y}
            stroke="#EDEDED"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          <text
            x={innerLeft - 4}
            y={y + 3}
            textAnchor="end"
            fontSize="8"
            fill="#A8ADB5"
          >
            {Number.isInteger(val) ? val : val.toFixed(1)}
          </text>
        </g>
      ))}

      {/* Y-axis label */}
      {yAxisLabel && (
        <text
          x={4}
          y={innerTop + innerH / 2}
          textAnchor="middle"
          fontSize="7"
          fill="#A8ADB5"
          transform={`rotate(-90, 4, ${innerTop + innerH / 2})`}
        >
          {yAxisLabel}
        </text>
      )}

      {/* Area fill */}
      {readings.length > 1 && (
        <polygon points={areaPoints} fill={`url(#${gradId})`} />
      )}

      {/* Line */}
      {readings.length > 1 && (
        <polyline
          points={polylinePoints}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Data point dots */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="4"
          fill="white"
          stroke={color}
          strokeWidth="1.5"
        />
      ))}

      {/* X-axis: first and last date labels */}
      {readings.length > 1 && (
        <>
          <text
            x={points[0].x}
            y={height - 6}
            textAnchor="start"
            fontSize="8"
            fill="#A8ADB5"
          >
            {formatAxisDate(readings[0].date)}
          </text>
          <text
            x={points[points.length - 1].x}
            y={height - 6}
            textAnchor="end"
            fontSize="8"
            fill="#A8ADB5"
          >
            {formatAxisDate(readings[readings.length - 1].date)}
          </text>
        </>
      )}
    </svg>
  );
}
